import { ImagePlus } from "lucide-react";
import { useCallback, useEffect } from "react";
import { ErrorCode, type FileRejection, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { cn } from "~/lib/utils";

interface ImageUploaderProps {
  onImageUpload: (file: File) => void;
}

export function ImageUploader({ onImageUpload }: ImageUploaderProps) {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.some(({ errors }) => errors.some(({ code }) => code === ErrorCode.TooManyFiles))) return;
      if (acceptedFiles.length > 0) onImageUpload(acceptedFiles[0]);
    },
    [onImageUpload],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file) onImageUpload(file);
          break;
        }
      }
    },
    [onImageUpload],
  );

  useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".tif", ".tiff"] },
    maxFiles: 1,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        "flex h-full cursor-pointer flex-col items-center justify-center gap-1 rounded-[var(--radius-md)] border-2 border-dashed p-8 text-center transition-colors",
        isDragActive ? "border-primary-500 bg-bg-tint" : "border-border-strong bg-bg-surface hover:bg-bg-subtle",
      )}
    >
      <input {...getInputProps()} />
      <ImagePlus className="mb-2 h-8 w-8 text-fg-3" aria-hidden />
      <p className="text-16-bold text-fg-1">{t("feature.georeferencer.uploadPrompt")}</p>
      <p className="text-14-regular text-fg-3">{t("ui.orDragAndDrop")}</p>
      <p className="text-14-regular text-fg-3">{t("ui.orPasteFromClipboard")}</p>
    </div>
  );
}
